import { defineStore } from 'pinia'
import { nowIso } from '@/utils/date'
import { useMutation } from '@/composables/useMutation'
import { useAuthStore } from './auth'
import { useObjectPoolStore } from './objectPool'
import type {
  PoolApiResponse,
  PoolExpense,
  PoolExpenseParticipant,
} from '@/types/pool'

export interface ExpenseParticipantInput {
  userId: string
  factor: number
}

export const useExpensesStore = defineStore('expenses', () => {
  const { loading, error, create, update, mutate } = useMutation()

  async function createExpense(
    eventId: string,
    description: string,
    amount: number,
    startDate: string,
    endDate: string,
    participants?: ExpenseParticipantInput[],
    onBehalfOfUserId?: string
  ) {
    const expenseId = crypto.randomUUID()
    const now = nowIso()
    const pool = useObjectPoolStore()
    const actorUserId = useAuthStore().currentUserId ?? null
    const subjectUserId = onBehalfOfUserId ?? actorUserId

    const tempExpense: PoolExpense = {
      id: expenseId,
      objectType: 'expense',
      eventId,
      userId: subjectUserId,
      createdByUserId: actorUserId,
      settlementId: null,
      revertsExpenseId: null,
      description,
      amount,
      startDate,
      endDate,
      participantIds: [],
      createdAt: now,
      updatedAt: now,
    }

    const tempParticipants: PoolExpenseParticipant[] = (participants ?? []).map(
      (p) => {
        const id = crypto.randomUUID()
        tempExpense.participantIds.push(id)
        return {
          id,
          objectType: 'expenseParticipant' as const,
          expenseId,
          userId: p.userId,
          factor: p.factor,
          createdAt: now,
          updatedAt: now,
        }
      }
    )

    if (tempParticipants.length > 0) {
      // Participants don't carry workspaceId — the pool derives the active
      // workspace's scope from useWorkspaceStore.
      pool.importObjects(tempParticipants)
    }

    const apiBody: Record<string, unknown> = {
      event_id: eventId,
      user_id: subjectUserId,
      description,
      amount,
      start_date: startDate,
      end_date: endDate,
      id: expenseId,
    }
    if (participants && participants.length > 0) {
      apiBody.participants = participants.map((p) => ({
        user_id: p.userId,
        factor: p.factor,
      }))
    }

    const result = await create(
      'Failed to create expense',
      tempExpense,
      (commandQueue) =>
        commandQueue.enqueue<PoolApiResponse>('POST', '/expenses', apiBody)
    )

    if (!result.queued) {
      for (const tp of tempParticipants) {
        pool.remove('expenseParticipant', tp.id)
      }
    }

    return { expenseId, queued: result.queued }
  }

  async function updateExpense(
    id: string,
    changes: {
      description?: string
      amount?: number
      startDate?: string
      endDate?: string
      participants?: ExpenseParticipantInput[]
    }
  ) {
    const apiChanges: Record<string, unknown> = {}
    if (changes.description !== undefined)
      apiChanges.description = changes.description
    if (changes.amount !== undefined) apiChanges.amount = changes.amount
    if (changes.startDate !== undefined)
      apiChanges.start_date = changes.startDate
    if (changes.endDate !== undefined) apiChanges.end_date = changes.endDate
    if (changes.participants !== undefined)
      apiChanges.participants = changes.participants.map((p) => ({
        user_id: p.userId,
        factor: p.factor,
      }))

    const poolChanges: Partial<PoolExpense> = {}
    if (changes.description !== undefined)
      poolChanges.description = changes.description
    if (changes.amount !== undefined) poolChanges.amount = changes.amount
    if (changes.startDate !== undefined)
      poolChanges.startDate = changes.startDate
    if (changes.endDate !== undefined) poolChanges.endDate = changes.endDate

    await update(
      'Failed to update expense',
      'expense',
      id,
      poolChanges,
      (commandQueue) =>
        commandQueue.enqueue<PoolApiResponse>(
          'PUT',
          `/expenses/${id}`,
          apiChanges
        )
    )
  }

  async function revertExpense(id: string) {
    return await mutate('Failed to revert expense', (commandQueue) =>
      commandQueue.enqueue<PoolApiResponse>('POST', `/expenses/${id}/revert`)
    )
  }

  function $reset() {
    loading.value = false
    error.value = null
  }

  return {
    loading,
    error,
    createExpense,
    updateExpense,
    revertExpense,
    $reset,
  }
})
